import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiErr } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { HandHeart, Check, Loader2, Pencil, Sparkles, MapPin } from "lucide-react";

const SOURCE_LABEL = {
  admin: "Your prayer of the day",
  milestone: "Trip milestone",
  prayer_request: "From a prayer request",
  ai_dispatch: "From your latest dispatch",
  library: "Today's prayer",
};

export default function DailyMission({ isAdmin, theme }) {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [burst, setBurst] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/daily-mission"); setM(data); setDraft(data.admin_set_text || ""); }
    catch (e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pray = async () => {
    setBusy(true);
    setBurst(true);
    setTimeout(() => setBurst(false), 1100);
    try {
      const { data } = await api.post("/daily-mission/complete");
      setM((p) => ({ ...p, completed: true, prayed_today_count: data.prayed_today_count, prayed_last_hour: data.prayed_last_hour }));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const savePrompt = async () => {
    setSavingPrompt(true);
    try {
      await api.post("/daily-mission", { text: draft });
      toast.success(draft.trim() ? "Prayer of the day set" : "Cleared — back to automatic");
      setEditing(false);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingPrompt(false); }
  };

  const regenerate = async () => {
    setSavingPrompt(true);
    try {
      const { data } = await api.post("/daily-mission/regenerate");
      setM((p) => ({ ...p, text: data.text, source: "ai_dispatch", admin_set_text: "" }));
      setDraft("");
      toast.success("Fresh prayer generated from your latest dispatch ✨");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingPrompt(false); }
  };

  if (loading) return null;
  if (!m) return null;

  const Burst = () => (
    <AnimatePresence>
      {burst && [...Array(8)].map((_, i) => (
        <motion.span key={i} className="absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-full" style={{ background: "#E0A73A" }}
          initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
          animate={{ opacity: 0, x: Math.cos((i / 8) * Math.PI * 2) * 46, y: Math.sin((i / 8) * Math.PI * 2) * 46, scale: 0.4 }}
          exit={{ opacity: 0 }} transition={{ duration: 0.9, ease: "easeOut" }} />
      ))}
    </AnimatePresence>
  );

  if (theme === "warm") {
    return (
      <div data-testid="daily-mission-card" className="rounded-3xl overflow-hidden relative shadow-[0_8px_28px_rgba(224,167,58,0.20)]" style={{ background: "linear-gradient(135deg,#FDF3E7,#FBE6CE)" }}>
        <img src="/assets/journey-bg.jpg" alt="" className="absolute right-0 bottom-0 w-3/5 h-full object-cover opacity-20 pointer-events-none" style={{ WebkitMaskImage: "linear-gradient(to left, #000 10%, transparent 85%)", maskImage: "linear-gradient(to left, #000 10%, transparent 85%)" }} />
        <div className="relative px-5 py-5">
          <div className="flex gap-4 items-start">
            <div className="shrink-0 relative">
              <div className="h-[72px] w-[72px] rounded-full bg-white flex items-center justify-center shadow-[0_4px_14px_rgba(224,167,58,0.25)]">
                <i className="fa-solid fa-hands-praying text-3xl" style={{ color: "#E0A73A" }} />
              </div>
              <span className="absolute -bottom-1 right-0 h-6 w-6 rounded-full bg-white flex items-center justify-center shadow"><MapPin className="h-3.5 w-3.5" style={{ color: "#E0A73A" }} fill="#E0A73A" /></span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.15em] font-bold" style={{ color: "#D98A1F" }}>Your mission today</div>
              <p className="font-heading font-bold text-2xl leading-snug mt-1" style={{ color: "#0d2340" }} data-testid="daily-mission-text">{m.text}</p>
              {m.estimated_seconds ? <div className="text-sm mt-1.5" style={{ color: "#6B7A8D" }}>Estimated time: {m.estimated_seconds} seconds</div> : null}
            </div>
          </div>
          {m.completed ? (
            <div className="mt-4 w-full rounded-xl h-12 flex items-center justify-center gap-2 font-bold text-white tracking-wide" style={{ background: "#E0A73A" }} data-testid="daily-mission-done">
              <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 380, damping: 14 }}><Check className="h-5 w-5" /></motion.span> I PRAYED
            </div>
          ) : (
            <div className="relative mt-4">
              <Burst />
              <motion.button data-testid="daily-mission-pray-btn" onClick={pray} disabled={busy} whileTap={{ scale: 0.96 }}
                className="relative w-full rounded-xl h-12 flex items-center justify-center gap-2 font-bold text-white tracking-wide" style={{ background: "#E0A73A" }}>
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><HandHeart className="h-5 w-5" /> {(m.action_label || "I Prayed").toUpperCase()}</>}
              </motion.button>
            </div>
          )}
          {m.prayed_last_hour > 0 || m.prayed_today_count > 0 ? <div className="mt-2"><PrayerPulse m={m} color="#8A97A6" /></div> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl overflow-hidden shadow-[0_8px_30px_rgba(0,58,88,0.16)]" data-testid="daily-mission-card" style={{ background: "linear-gradient(135deg,#0d1b2a,#12324a)" }}>
      <div className="px-6 py-5 text-white">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-white/60">
          <Sparkles className="h-3.5 w-3.5" style={{ color: "var(--brand-gold)" }} /> Your mission today
        </div>
        <p className="font-heading text-xl leading-snug mt-2" data-testid="daily-mission-text">{m.text}</p>

        {m.completed ? (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="mt-4 flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--brand-gold)" }} data-testid="daily-mission-done">
            <motion.span initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 380, damping: 14 }}>
              <Check className="h-4 w-4" />
            </motion.span>
            You prayed today — thank you.
          </motion.div>
        ) : (
          <div className="relative inline-block mt-4">
            <AnimatePresence>
              {burst && [...Array(8)].map((_, i) => (
                <motion.span key={i} className="absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-full"
                  style={{ background: "var(--brand-gold)" }}
                  initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
                  animate={{ opacity: 0, x: Math.cos((i / 8) * Math.PI * 2) * 46, y: Math.sin((i / 8) * Math.PI * 2) * 46, scale: 0.4 }}
                  exit={{ opacity: 0 }} transition={{ duration: 0.9, ease: "easeOut" }} />
              ))}
            </AnimatePresence>
            <motion.button data-testid="daily-mission-pray-btn" onClick={pray} disabled={busy} whileTap={{ scale: 0.94 }}
              className="relative inline-flex items-center gap-2 rounded-full px-6 h-11 font-semibold text-[#0d1b2a]"
              style={{ background: "var(--brand-gold)" }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <HandHeart className="h-4 w-4" />} {m.action_label || "I Prayed"}
            </motion.button>
          </div>
        )}

        <div className="mt-3 flex items-center gap-3 text-[11px] text-white/50">
          {m.estimated_seconds ? <span>≈ {m.estimated_seconds} seconds</span> : null}
          {(m.prayed_last_hour > 0 || m.prayed_today_count > 0) && <PrayerPulse m={m} color="rgba(255,255,255,0.55)" prefix="· " />}
        </div>
      </div>

      {isAdmin && (
        <div className="bg-black/20 px-6 py-3 border-t border-white/10">
          {editing ? (
            <div className="space-y-2" data-testid="daily-mission-admin-editor">
              <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2}
                placeholder="Write today's prayer for your partners… (leave empty to use automatic)"
                className="rounded-xl bg-white/95 text-[#2C2C3E] border-0 text-sm" data-testid="daily-mission-admin-input" />
              <div className="flex items-center gap-2">
                <Button data-testid="daily-mission-admin-save" onClick={savePrompt} disabled={savingPrompt} className="rounded-full h-9 px-4 text-[#0d1b2a]" style={{ background: "var(--brand-gold)" }}>{savingPrompt ? "Saving…" : "Save"}</Button>
                <button onClick={() => { setEditing(false); setDraft(m.admin_set_text || ""); }} className="text-xs text-white/60 hover:text-white">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <button data-testid="daily-mission-admin-edit" onClick={() => setEditing(true)} className="flex items-center gap-2 text-xs text-white/70 hover:text-white">
                <Pencil className="h-3.5 w-3.5" /> {SOURCE_LABEL[m.source]} · <span className="underline">write today's prayer</span>
              </button>
              <button data-testid="daily-mission-regenerate" onClick={regenerate} disabled={savingPrompt}
                className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white disabled:opacity-50">
                {savingPrompt ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Regenerate from latest post
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Live "praying together" feel — pulses on the last-hour count, falls back to today's total.
function PrayerPulse({ m, color, prefix = "" }) {
  const live = m.prayed_last_hour > 0;
  const n = live ? m.prayed_last_hour : m.prayed_today_count;
  const label = live ? `${n === 1 ? "person" : "partners"} prayed in the last hour` : "praying today";
  return (
    <span className="inline-flex items-center gap-1.5" style={{ color }} data-testid="daily-mission-count">
      {prefix}
      {live && (
        <span className="relative flex h-2 w-2" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full rounded-full animate-ping" style={{ background: "#E0A73A", opacity: 0.75 }} />
          <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#E0A73A" }} />
        </span>
      )}
      <AnimatePresence mode="popLayout">
        <motion.span key={n} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }} transition={{ duration: 0.35 }} className="font-semibold tabular-nums">
          {n}
        </motion.span>
      </AnimatePresence>
      <span className="ml-1">{label}</span>
    </span>
  );
}
